// Apple Lossless (ALAC) in an .m4a file -> a 16-bit WAV, so browsers that can't decode ALAC in <audio>
// (Chrome, Firefox) can still play those uploads. Safari and iOS play ALAC natively and never get here.
//
// The decoder is a port of Apple's open-source ALAC decoder (http://alac.macosforge.org, Apache License 2.0),
// by way of the Aurora.js JavaScript port (https://github.com/ofmlabs/alac.js, Jens Nockert and Devon Govett).
// Only 16-bit audio is supported; anything else throws and the player shows its usual "couldn't load" error.

const QBSHIFT = 9;
const QB = 1 << QBSHIFT;
const MMULSHIFT = 2;
const MDENSHIFT = QBSHIFT - MMULSHIFT - 1;
const MOFF = 1 << (MDENSHIFT - 2);
const BITOFF = 24;
const MAX_PREFIX = 9;
const MAX_DATATYPE_BITS_16 = 16;
const N_MAX_MEAN_CLAMP = 0xffff;
const N_MEAN_CLAMP_VAL = 0xffff;

const ID_SCE = 0, ID_CPE = 1, ID_CCE = 2, ID_LFE = 3, ID_DSE = 4, ID_PCE = 5, ID_FIL = 6, ID_END = 7;

// ---------------------------------------------------------------------------------------------- bit reader

class BitReader {
  constructor(bytes, start, end) {
    this.b = bytes;
    this.pos = start * 8; // in bits
    this.end = end * 8;
  }

  // The next 32 bits, without consuming them (zeros past the end of the data).
  peek32() {
    const b = this.b;
    const i = this.pos >>> 3;
    const s = this.pos & 7;
    let v = ((b[i] << 24) | (b[i + 1] << 16) | (b[i + 2] << 8) | b[i + 3]) >>> 0;
    if (s) v = ((v << s) | ((b[i + 4] | 0) >>> (8 - s))) >>> 0;
    return v;
  }

  read(n) {
    if (n === 0) return 0;
    const v = this.peek32();
    this.pos += n;
    return n === 32 ? v : v >>> (32 - n);
  }

  align() { this.pos = (this.pos + 7) & ~7; }
}

// ------------------------------------------------------------------------------------ adaptive Golomb decode

function dynGet16(r, m, k) {
  const stream = r.peek32();
  const pre = Math.clz32(~stream);
  if (pre >= MAX_PREFIX) {
    r.pos += MAX_PREFIX + MAX_DATATYPE_BITS_16;
    return (stream << MAX_PREFIX) >>> (32 - MAX_DATATYPE_BITS_16);
  }
  r.pos += pre + k;
  const v = k === 0 ? 0 : (stream << (pre + 1)) >>> (32 - k);
  let result = pre * m + v - 1;
  if (v < 2) result -= v - 1;
  else r.pos += 1;
  return result;
}

function dynGet32(r, m, k, maxBits) {
  const stream = r.peek32();
  let result = Math.clz32(~stream);
  if (result >= MAX_PREFIX) {
    r.pos += MAX_PREFIX;
    return r.read(maxBits);
  }
  r.pos += result + 1;
  if (k !== 1) {
    const v = (stream << (result + 1)) >>> (32 - k);
    result *= m;
    r.pos += k - 1;
    if (v > 1) {
      result += v - 1;
      r.pos += 1;
    }
  }
  return result;
}

function dynDecomp(r, pc, samples, maxBits, mb0, pb, kb) {
  const wb = (1 << kb) - 1;
  let mb = mb0;
  let zmode = 0;
  let c = 0;
  while (c < samples) {
    let m = mb >>> QBSHIFT;
    const k = Math.min(31 - Math.clz32(m + 3), kb);
    m = (1 << k) - 1;
    const n = dynGet32(r, m, k, maxBits);

    // least significant bit is the sign bit
    const ndecode = n + zmode;
    const multiplier = -(ndecode & 1) | 1;
    pc[c++] = ((ndecode + 1) >>> 1) * multiplier;

    mb = pb * (n + zmode) + mb - ((pb * mb) >> QBSHIFT);
    if (n > N_MAX_MEAN_CLAMP) mb = N_MEAN_CLAMP_VAL;

    zmode = 0;
    if (((mb << MMULSHIFT) < QB) && c < samples) {
      zmode = 1;
      const kz = Math.clz32(mb) - BITOFF + ((mb + MOFF) >> MDENSHIFT);
      const mz = ((1 << kz) - 1) & wb;
      const run = dynGet16(r, mz, kz);
      if (c + run > samples) return false;
      for (let j = 0; j < run; j++) pc[c++] = 0;
      if (run >= 65535) zmode = 0;
      mb = 0;
    }
  }
  return true;
}

// ------------------------------------------------------------------------------------------ dynamic predictor

function unpcBlock(pc1, out, num, coefs, active, chanBits, denShift) {
  const chanShift = 32 - chanBits;
  const denHalf = 1 << (denShift - 1);

  out[0] = pc1[0];
  if (active === 0) {
    if (out !== pc1) out.set(pc1.subarray(0, num));
    return;
  }

  // the special "numActive == 31" mode is a plain running sum
  if (active === 31) {
    let prev = out[0];
    for (let i = 1; i < num; i++) {
      const del = pc1[i] + prev;
      prev = (del << chanShift) >> chanShift;
      out[i] = prev;
    }
    return;
  }

  for (let i = 1; i <= active; i++) {
    const del = pc1[i] + out[i - 1];
    out[i] = (del << chanShift) >> chanShift;
  }

  const lim = active + 1;
  for (let i = lim; i < num; i++) {
    let sum1 = 0;
    const top = out[i - lim];
    const offset = i - 1;
    for (let j = 0; j < active; j++) sum1 += coefs[j] * (out[offset - j] - top);

    let del = pc1[i];
    let del0 = del;
    const sg = (-del >>> 31) | (del >> 31);
    del += top + ((sum1 + denHalf) >> denShift);
    out[i] = (del << chanShift) >> chanShift;

    if (sg > 0) {
      for (let j = active - 1; j >= 0; j--) {
        const dd = top - out[offset - j];
        const sgn = (-dd >>> 31) | (dd >> 31);
        coefs[j] -= sgn;
        del0 -= (active - j) * ((sgn * dd) >> denShift);
        if (del0 <= 0) break;
      }
    } else if (sg < 0) {
      for (let j = active - 1; j >= 0; j--) {
        const dd = top - out[offset - j];
        const sgn = (-dd >>> 31) | (dd >> 31);
        coefs[j] += sgn;
        del0 -= (active - j) * ((-sgn * dd) >> denShift);
        if (del0 >= 0) break;
      }
    }
  }
}

// ------------------------------------------------------------------------------------------------ MP4 reading

const u32 = (b, o) => ((b[o] << 24) | (b[o + 1] << 16) | (b[o + 2] << 8) | b[o + 3]) >>> 0;
const u16 = (b, o) => (b[o] << 8) | b[o + 1];
const tag = (b, o) => String.fromCharCode(b[o], b[o + 1], b[o + 2], b[o + 3]);

// Calls fn(type, bodyStart, bodyEnd) for each box in [start, end).
function eachBox(b, start, end, fn) {
  let o = start;
  while (o + 8 <= end) {
    let size = u32(b, o);
    let head = 8;
    if (size === 1) {
      size = u32(b, o + 8) * 4294967296 + u32(b, o + 12);
      head = 16;
    } else if (size === 0) {
      size = end - o;
    }
    if (size < head) break;
    const boxEnd = Math.min(o + size, end);
    if (fn(tag(b, o + 4), o + head, boxEnd) === false) return;
    o += size;
  }
}

function findBox(b, start, end, type) {
  let found = null;
  eachBox(b, start, end, (t, s, e) => {
    if (t === type) { found = [s, e]; return false; }
    return true;
  });
  return found;
}

function parseM4a(b) {
  const moov = findBox(b, 0, b.length, 'moov');
  if (!moov) throw new Error('Not an MP4 file');

  let result = null;
  eachBox(b, moov[0], moov[1], (type, s, e) => {
    if (type !== 'trak' || result) return true;
    const mdia = findBox(b, s, e, 'mdia');
    const minf = mdia && findBox(b, mdia[0], mdia[1], 'minf');
    const stbl = minf && findBox(b, minf[0], minf[1], 'stbl');
    const stsd = stbl && findBox(b, stbl[0], stbl[1], 'stsd');
    // stsd: version/flags, entry count, then the first sample entry: size, type, ...
    if (!stsd || tag(b, stsd[0] + 12) !== 'alac') return true;

    const entry = stsd[0] + 8;
    const entryEnd = Math.min(entry + u32(b, entry), stsd[1]);
    const soundVersion = u16(b, entry + 16);
    const childStart = entry + 36 + (soundVersion === 1 ? 16 : soundVersion === 2 ? 36 : 0);
    const cookieBox = findBox(b, childStart, entryEnd, 'alac');
    if (!cookieBox) throw new Error('ALAC setup data is missing');
    const c = cookieBox[0] + 4; // skip the version/flags
    const config = {
      frameLength: u32(b, c),
      bitDepth: b[c + 5],
      pb: b[c + 6],
      mb: b[c + 7],
      kb: b[c + 8],
      numChannels: b[c + 9],
      maxRun: u16(b, c + 10),
      sampleRate: u32(b, c + 20),
    };

    const stsz = findBox(b, stbl[0], stbl[1], 'stsz');
    const stsc = findBox(b, stbl[0], stbl[1], 'stsc');
    const stco = findBox(b, stbl[0], stbl[1], 'stco');
    const co64 = stco ? null : findBox(b, stbl[0], stbl[1], 'co64');
    if (!stsz || !stsc || !(stco || co64)) throw new Error('Incomplete MP4 sample table');

    // sample sizes
    const fixedSize = u32(b, stsz[0] + 4);
    const count = u32(b, stsz[0] + 8);
    const sizes = new Array(count);
    for (let i = 0; i < count; i++) sizes[i] = fixedSize || u32(b, stsz[0] + 12 + i * 4);

    // chunk offsets, and how many samples sit in each chunk
    const chunkBox = stco || co64;
    const chunkCount = u32(b, chunkBox[0] + 4);
    const chunkOffset = (i) => (stco
      ? u32(b, chunkBox[0] + 8 + i * 4)
      : u32(b, chunkBox[0] + 8 + i * 8) * 4294967296 + u32(b, chunkBox[0] + 12 + i * 8));
    const runs = u32(b, stsc[0] + 4);
    const runFirst = (r) => u32(b, stsc[0] + 8 + r * 12);
    const runSamples = (r) => u32(b, stsc[0] + 12 + r * 12);

    const offsets = new Array(count);
    let sample = 0;
    let run = 0;
    for (let chunk = 1; chunk <= chunkCount && sample < count; chunk++) {
      while (run + 1 < runs && runFirst(run + 1) <= chunk) run++;
      let o = chunkOffset(chunk - 1);
      for (let n = runSamples(run); n > 0 && sample < count; n--) {
        offsets[sample] = o;
        o += sizes[sample];
        sample++;
      }
    }
    if (sample < count) throw new Error('Incomplete MP4 sample table');
    result = { config, offsets, sizes };
    return false;
  });

  if (!result) throw new Error('Not an Apple Lossless file');
  return result;
}

// ------------------------------------------------------------------------------------------ packet decoding

// Decodes one packet into out (interleaved Int16) at frame offset `at`; returns the number of frames.
function decodePacket(r, cfg, buffers, out, at) {
  let samples = cfg.frameLength;
  const numChannels = cfg.numChannels;
  const { predictor, mix } = buffers;
  let channelIndex = 0;

  for (;;) {
    const id = r.read(3);
    if (id === ID_SCE || id === ID_LFE || id === ID_CPE) {
      const channels = id === ID_CPE ? 2 : 1;
      if (channelIndex + channels > numChannels) throw new Error('Too many channels');
      r.read(4); // element instance tag
      if (r.read(12) !== 0) throw new Error('Corrupt ALAC header');
      const partialFrame = r.read(1);
      const bytesShifted = r.read(2);
      const escapeFlag = r.read(1);
      if (bytesShifted !== 0) throw new Error('Unsupported ALAC shift');
      if (partialFrame) samples = r.read(32);
      if (samples > cfg.frameLength) throw new Error('Corrupt ALAC frame length');

      let mixBits = 0;
      let mixRes = 0;
      if (escapeFlag === 0) {
        const chanBits = cfg.bitDepth + channels - 1;
        mixBits = r.read(8);
        mixRes = (r.read(8) << 24) >> 24; // signed
        const mode = [];
        const denShift = [];
        const pbFactor = [];
        const num = [];
        const coefs = [];
        for (let ch = 0; ch < channels; ch++) {
          mode[ch] = r.read(4);
          denShift[ch] = r.read(4);
          pbFactor[ch] = r.read(3);
          num[ch] = r.read(5);
          const table = new Int16Array(32);
          for (let i = 0; i < num[ch]; i++) table[i] = r.read(16);
          coefs[ch] = table;
        }
        for (let ch = 0; ch < channels; ch++) {
          const pb = Math.trunc((cfg.pb * pbFactor[ch]) / 4);
          if (!dynDecomp(r, predictor, samples, chanBits, cfg.mb, pb, cfg.kb)) throw new Error('Corrupt ALAC data');
          if (mode[ch] === 0) {
            unpcBlock(predictor, mix[ch], samples, coefs[ch], num[ch], chanBits, denShift[ch]);
          } else {
            unpcBlock(predictor, predictor, samples, null, 31, chanBits, 0);
            unpcBlock(predictor, mix[ch], samples, coefs[ch], num[ch], chanBits, denShift[ch]);
          }
        }
      } else {
        // uncompressed: copy into the mix buffers so the output code below is shared
        const shift = 32 - cfg.bitDepth;
        for (let i = 0; i < samples; i++) {
          for (let ch = 0; ch < channels; ch++) mix[ch][i] = (r.read(cfg.bitDepth) << shift) >> shift;
        }
      }

      const base = at * numChannels + channelIndex;
      if (channels === 2) {
        const u = mix[0];
        const v = mix[1];
        if (mixRes === 0) {
          for (let i = 0; i < samples; i++) {
            out[base + i * numChannels] = u[i];
            out[base + i * numChannels + 1] = v[i];
          }
        } else {
          for (let i = 0; i < samples; i++) {
            const l = u[i] + v[i] - ((mixRes * v[i]) >> mixBits);
            out[base + i * numChannels] = l;
            out[base + i * numChannels + 1] = l - v[i];
          }
        }
      } else {
        const u = mix[0];
        for (let i = 0; i < samples; i++) out[base + i * numChannels] = u[i];
      }
      channelIndex += channels;
    } else if (id === ID_CCE || id === ID_PCE) {
      throw new Error('Unsupported ALAC element');
    } else if (id === ID_DSE) {
      r.read(4);
      const align = r.read(1);
      let count = r.read(8);
      if (count === 255) count += r.read(8);
      if (align) r.align();
      r.pos += count * 8;
    } else if (id === ID_FIL) {
      let count = r.read(4);
      if (count === 15) count += r.read(8) - 1;
      r.pos += count * 8;
    } else if (id === ID_END) {
      r.align();
      return samples;
    }
    if (r.pos > r.end) throw new Error('Corrupt ALAC data');
  }
}

const pause = () => new Promise((resolve) => setTimeout(resolve, 0));

// Decodes an ALAC .m4a (an ArrayBuffer) to { channels, sampleRate, samples: Int16Array (interleaved) }.
// `isCancelled` is polled between batches so a skipped song stops decoding. Throws if it isn't 16-bit ALAC.
export async function decodeAlac(arrayBuffer, isCancelled = () => false) {
  const bytes = new Uint8Array(arrayBuffer);
  const { config, offsets, sizes } = parseM4a(bytes);
  if (config.bitDepth !== 16) throw new Error(`${config.bitDepth}-bit ALAC isn't supported`);
  if (config.numChannels < 1 || config.numChannels > 2) throw new Error('Only mono and stereo ALAC are supported');

  const buffers = {
    predictor: new Int32Array(config.frameLength),
    mix: [new Int32Array(config.frameLength), new Int32Array(config.frameLength)],
  };
  const out = new Int16Array(offsets.length * config.frameLength * config.numChannels);
  let frames = 0;
  for (let i = 0; i < offsets.length; i++) {
    const r = new BitReader(bytes, offsets[i], Math.min(offsets[i] + sizes[i], bytes.length));
    frames += decodePacket(r, config, buffers, out, frames);
    if (i % 100 === 99) {
      await pause(); // keep the page responsive during a long song
      if (isCancelled()) return null;
    }
  }
  return {
    channels: config.numChannels,
    sampleRate: config.sampleRate,
    samples: out.subarray(0, frames * config.numChannels),
  };
}

// 16-bit PCM WAV file wrapping decoded audio.
export function toWavBlob({ channels, sampleRate, samples }) {
  const dataBytes = samples.length * 2;
  const header = new DataView(new ArrayBuffer(44));
  const text = (o, s) => { for (let i = 0; i < s.length; i++) header.setUint8(o + i, s.charCodeAt(i)); };
  text(0, 'RIFF');
  header.setUint32(4, 36 + dataBytes, true);
  text(8, 'WAVE');
  text(12, 'fmt ');
  header.setUint32(16, 16, true);
  header.setUint16(20, 1, true); // PCM
  header.setUint16(22, channels, true);
  header.setUint32(24, sampleRate, true);
  header.setUint32(28, sampleRate * channels * 2, true);
  header.setUint16(32, channels * 2, true);
  header.setUint16(34, 16, true);
  text(36, 'data');
  header.setUint32(40, dataBytes, true);
  return new Blob([header.buffer, samples], { type: 'audio/wav' });
}

// Fetches a song file and, if it is ALAC, returns an object URL for a WAV copy the browser can play.
// Resolves to null if it was cancelled; rejects if the file isn't something this can decode.
async function alacObjectUrl(url, isCancelled) {
  const response = await fetch(url);
  if (!response.ok) throw new Error(`Download failed (${response.status})`);
  // Only MP4 audio can be ALAC; don't download a whole file that failed for some other reason.
  if (!/mp4|m4a|aac|octet-stream/i.test(response.headers.get('content-type') || '')) throw new Error('Not an m4a file');
  const buffer = await response.arrayBuffer();
  if (isCancelled()) return null;
  const decoded = await decodeAlac(buffer, isCancelled);
  if (!decoded || isCancelled()) return null;
  return URL.createObjectURL(toWavBlob(decoded));
}

// A playable replacement for a song file the browser rejected, or null if cancelled. Only the latest song is
// kept (a decoded 4-minute song is about 40 MB), so playing it again is instant but the one before it is freed.
let cached = null; // { url, objectUrl }
export async function alacPlayableUrl(url, isCancelled = () => false) {
  if (cached && cached.url === url) return cached.objectUrl;
  const objectUrl = await alacObjectUrl(url, isCancelled);
  if (!objectUrl) return null;
  if (cached) URL.revokeObjectURL(cached.objectUrl);
  cached = { url, objectUrl };
  return objectUrl;
}
